import type { Artifact } from "@lumen/shared";
import type { ComposerSkill } from "./Composer";
import type { WorkspaceFileReference } from "./ComposerContextTypes";
import {
  TextNode,
  type DOMConversionMap,
  type NodeKey,
  type SerializedTextNode,
} from "lexical";
import type { TextMatchTransformer } from "@lexical/markdown";

export type ComposerMention = {
  reference: string;
  label: string;
  kind: "file" | "artifact" | "skill";
};

type SerializedMention = SerializedTextNode & ComposerMention;

/** Atomic inline context. The label is presentation; the reference is the wire format. */
export class ComposerMentionNode extends TextNode {
  __reference: string;
  __kind: ComposerMention["kind"];

  static getType() {
    return "composer-mention";
  }
  static clone(node: ComposerMentionNode) {
    return new ComposerMentionNode(
      { reference: node.__reference, label: node.__text, kind: node.__kind },
      node.__key,
    );
  }
  constructor(mention: ComposerMention, key?: NodeKey) {
    super(mention.label, key);
    this.__reference = mention.reference;
    this.__kind = mention.kind;
    this.__mode = 1; // Lexical token: delete and select the whole reference together.
  }
  static importJSON(serialized: SerializedMention) {
    return new ComposerMentionNode(serialized).updateFromJSON(serialized);
  }
  static importDOM(): DOMConversionMap {
    const convert = (element: HTMLElement) => {
      const reference = element.dataset.reference;
      const kind = element.dataset.mentionKind;
      if (
        !reference ||
        !(
          (kind === "skill" && /^\/[a-z0-9-]+$/i.test(reference)) ||
          (kind === "artifact" &&
            /^@artifact:[a-z0-9._-]+$/i.test(reference)) ||
          (kind === "file" && /^@[a-z0-9._~/-]+$/i.test(reference))
        )
      )
        return null;
      return {
        priority: 2 as const,
        conversion: () => ({
          node: new ComposerMentionNode({
            reference,
            kind,
            label: element.textContent || reference,
          }),
          // The node already owns the chip's label.
          forChild: () => null,
        }),
      };
    };
    return { button: convert, span: convert };
  }
  exportJSON(): SerializedMention {
    return {
      ...super.exportJSON(),
      reference: this.__reference,
      label: this.__text,
      kind: this.__kind,
    };
  }
  createDOM(config: Parameters<TextNode["createDOM"]>[0]) {
    const element = super.createDOM(config);
    element.classList.add("composer-mention");
    element.dataset.mentionKind = this.__kind;
    element.dataset.reference = this.__reference;
    element.title = this.__reference;
    element.spellcheck = false;
    return element;
  }
  canInsertTextBefore() {
    return false;
  }
  canInsertTextAfter() {
    return false;
  }
}

export function mentionTransformer(
  mentions: ComposerMention[],
): TextMatchTransformer {
  return {
    type: "text-match",
    dependencies: [ComposerMentionNode],
    // Import saved drafts, never turn a half-typed query into a token.
    importRegExp:
      /(?<!\S)(?:\/[a-z0-9-]+|@artifact:[a-z0-9](?:[a-z0-9._-]*[a-z0-9_-])?|@[a-z0-9._~/-]*[a-z0-9_~/-])(?=\s|$|[.,!?;:)])/i,
    regExp: /(?!)/,
    export: (node) =>
      node instanceof ComposerMentionNode ? node.__reference : null,
    replace: (node, match) => {
      const reference = match[0];
      const mention = mentions.find((item) => item.reference === reference);
      node.replace(
        new ComposerMentionNode(
          mention ?? {
            reference,
            label: reference,
            kind: reference.startsWith("/")
              ? "skill"
              : reference.startsWith("@artifact:")
                ? "artifact"
                : "file",
          },
        ),
      );
    },
  };
}

export function composerMentionCatalog(
  skills: ComposerSkill[],
  artifacts: Artifact[],
  files: WorkspaceFileReference[],
): ComposerMention[] {
  return [
    ...skills.map((skill): ComposerMention => ({
      reference: `/${skill.slug}`,
      label: `/${skill.slug}`,
      kind: "skill",
    })),
    ...artifacts.map((artifact): ComposerMention => ({
      reference: `@artifact:${artifact.id}`,
      label: artifact.name,
      kind: "artifact",
    })),
    ...files.map((file): ComposerMention => ({
      reference: `@${file.path}`,
      label: `@${file.path}`,
      kind: "file",
    })),
  ];
}

/** Repository skills override a database skill with the same slug, as in the picker. */
export function mentionedSkillIds(
  content: string,
  skills: ComposerSkill[],
  fallback: string[],
) {
  // Code is literal, including standalone slash commands within it.
  const prose = content
    .replace(/^(`{3,}|~{3,})[^\n]*\n[\s\S]*?^\1[^\n]*(?:\n|$)/gm, "")
    .replace(/(?<!`)(`+)(?!`)[\s\S]*?(?<!`)\1(?!`)/g, "");
  const slugs = new Set(
    [...prose.matchAll(/(?:^|\s)\/([a-z0-9-]+)(?=\s|$)/gi)].map((match) =>
      match[1].toLowerCase(),
    ),
  );
  const bySlug = new Map(
    skills.map((skill) => [skill.slug.toLowerCase(), skill.id]),
  );
  const ids = [
    ...new Set(
      [...slugs].flatMap((slug) =>
        bySlug.has(slug) ? [bySlug.get(slug)!] : [],
      ),
    ),
  ];
  return ids.length ? ids : fallback;
}
